import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RedoMarketplaceRpcClientContext } from "@redotech/redo-marketplace-app-common/rpc-client";
import { Button } from "@redotech/redo-web/components/ui/button";
import { Input } from "@redotech/redo-web/components/ui/input";
import { Label } from "@redotech/redo-web/components/ui/label";
import { Separator } from "@redotech/redo-web/components/ui/separator";
import { memo, useState } from "react";

export const Login = memo(function Login() {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const marketplaceRpcClient = useRequiredContext(
    RedoMarketplaceRpcClientContext,
  );

  const [sendMagicLinkResult, triggerSendMagicLink] = useTriggerLoad(
    async () => {
      return await marketplaceRpcClient.sendMagicLink({ email });
    },
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      return;
    }

    triggerSendMagicLink();
    setIsSubmitted(true);
  };

  if (isSubmitted && sendMagicLinkResult.value?.success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200">
            {/* Success Icon */}
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-6">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20 6L9 17L4 12"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </svg>
            </div>

            <div className="text-center space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">
                Check your email
              </h1>

              <p className="text-sm text-gray-600">
                We've sent a magic link to{" "}
                <strong className="text-gray-900">{email}</strong>. Click the
                link in the email to sign in to your marketplace account.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-xs text-blue-800">
                  The link will expire in 1 hour. If you don't see the email,
                  check your spam folder.
                </p>
              </div>

              <Separator className="my-6" />

              <Button
                className="w-full"
                onClick={() => {
                  setIsSubmitted(false);
                  setEmail("");
                }}
                variant="outline"
              >
                Use a different email address
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo Section */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-100">
            <svg
              className="h-8 w-8 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
              <path
                d="M9 22V12H15V22"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </div>
        </div>

        <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200">
          <div className="space-y-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900">
                Welcome to Marketplace
              </h1>

              <p className="mt-2 text-sm text-gray-600">
                Enter your email address and we'll send you a magic link to sign
                in.
              </p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <Label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  htmlFor="email"
                >
                  Email address
                </Label>
                <Input
                  autoComplete="email"
                  autoFocus
                  className="w-full"
                  id="email"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  type="email"
                  value={email}
                />
              </div>

              <Button
                className="w-full"
                disabled={!email.trim() || sendMagicLinkResult.pending}
                size="lg"
                type="submit"
              >
                {sendMagicLinkResult.pending ? (
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        fill="currentColor"
                      />
                    </svg>
                    <span>Sending...</span>
                  </div>
                ) : (
                  "Send magic link"
                )}
              </Button>
            </form>

            {sendMagicLinkResult.error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <line
                        stroke="currentColor"
                        strokeWidth="2"
                        x1="15"
                        x2="9"
                        y1="9"
                        y2="15"
                      />
                      <line
                        stroke="currentColor"
                        strokeWidth="2"
                        x1="9"
                        x2="15"
                        y1="9"
                        y2="15"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">
                      {sendMagicLinkResult.error.message ||
                        "Something went wrong. Please try again."}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
